import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import React, {FC, useState, useEffect, useMemo} from 'react'
import clsx from 'clsx'
import _ from 'lodash'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'

import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button} from 'react-bootstrap'
import {Link, useParams} from 'react-router-dom'
import {Toast} from '../../../../ui-kit'
import {exportTemplate, getHSDescriptions, getTariffList, importDataTariff} from './core/_requests'

import EconDropdown from '../../../../ui-kit/EconDropdown/EconDropdown'
import ListBoundTariffTable from './components/ListBoundTariffTable'
import ListMFNTariffTable from './components/ListMFNTariffTable'
import ListPrefTariffTable from './components/ListPrefTariffTable'
import ModalUploadTariff from './components/ModalUploadTariff'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import NoPermissionCard from '../../../../_metronic/layout/components/permission/NoPermissionCard'

const ReactSwal = withReactContent(Swal)
const TariffPage: FC<{subject: string}> = ({subject}) => {
  const {typeTariff} = useParams()
  const ability = useAbility(AbilityContext)

  const canAccess =
    ability.can('read', String(subject).replace('.', `.${typeTariff?.toUpperCase()}_`)) ||
    ability.can('create', String(subject).replace('.', `.${typeTariff?.toUpperCase()}_`))

  const axiosPrivate = useAxiosPrivate()
  // const [typeTariff, setTypeTariff] = useState<'bound' | 'mfn' | 'pref'>('bound')
  const [tariffParams, setTariffParams] = useState<any>({
    tariff_type: 'bound',
    rep: null,
    par: null,
    hs_ver: null,
  })

  const [tariffTableData, setTariffTableData] = useState<any>({
    bound: [],
    mfn: [],
    pref: [],
  })
  const [isShowTable, setIsShowTable] = useState<boolean>(false)
  const [tableLoading, setTableLoading] = useState(false)
  let pageTitle: string = 'Tariff'
  let pageTable: React.ReactNode = null

  switch (typeTariff) {
    case 'bound':
      pageTitle = 'Bound Tariff'
      pageTable = <ListBoundTariffTable data={tariffTableData?.bound} isLoading={tableLoading} />
      break
    case 'mfn':
      pageTitle = 'MFN Tariff'
      pageTable = <ListMFNTariffTable data={tariffTableData?.mfn} isLoading={tableLoading} />
      break
    case 'pref':
      pageTitle = 'Preferential Tariff'
      pageTable = <ListPrefTariffTable data={tariffTableData?.pref} isLoading={tableLoading} />
      break
  }

  const btnResetHandler = () => {
    setTariffParams({
      tariff_type: typeTariff,
      rep: null,
      par: null,
      hs_ver: null,
    })
    setIsShowTable(false)
  }

  const btnSearchHandler = async () => {
    setTableLoading(true)
    setIsShowTable(true)
    try {
      const res = await getTariffList(typeTariff!, tariffParams)
      const hsList = _.uniq(res.data.map((item) => item.hs))
      const resDesc = await getHSDescriptions({hs_ver: tariffParams.hs_ver, hs_codes: hsList})
      const dataTable = res.data.map((item: any) => {
        return {
          ...item,
          hs_description: resDesc.data[item.hs] || '',
        }
      })
      setTariffTableData({
        ...tariffTableData,
        [typeTariff!]: dataTable,
      })
    } catch (e) {
      console.error(e)
      Toast('Error when fetching tariff data', 'danger', '')
    } finally {
      setTableLoading(false)
    }
  }

  useEffect(() => {
    if (typeTariff !== 'pref' && tariffParams.rep !== null && tariffParams.hs_ver !== null) {
      btnSearchHandler()
      setTariffParams((prev) => ({
        ...prev,
        tariff_type: typeTariff,
        par: null,
      }))
    } else {
      btnResetHandler()
    }
    // setIsShowTable(false)
  }, [typeTariff])

  const isSearchEnabled = useMemo(() => {
    // HS Version is always required
    if (!tariffParams.hs_ver) return false

    // Reporter is always required
    if (!tariffParams.rep) return false

    // Partner only required for Preferential
    if (typeTariff === 'pref' && !tariffParams.par) return false

    return true
  }, [tariffParams, typeTariff])

  const [showUploadTariffModal, setShowImportModal] = useState<boolean>(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const handleDownloadTemplate = () => {
    exportTemplate(axiosPrivate, typeTariff)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `tariff-${typeTariff}-template.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch((err) => {
        console.error('Failed to download template:', err)
        ReactSwal.fire('Error', 'Failed to download template', 'error')
      })
  }

  const handleSubmitImport = () => {
    if (!importFile) {
      setImportError('Please select a file before importing.')
      return
    }

    setIsImporting(true)
    setImportError(null)

    const formData = new FormData()
    formData.append('file', importFile)
    formData.append('type', tariffParams.tariff_type)

    importDataTariff(axiosPrivate, formData)
      .then((res) => {
        if (res.status === 200) {
          setShowImportModal(false)
          ReactSwal.fire({
            title: 'Import Successful',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            if (tariffParams.rep && tariffParams.hs_ver) {
              btnSearchHandler()
            }
          })
        }
      })
      .catch((resErr: any) => {
        const message = resErr?.response?.data?.message || 'Upload failed. Please check your file.'
        setImportError(message)
      })
      .finally(() => {
        setIsImporting(false)
      })
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {pageTitle}
      </PageTitle>
      {canAccess ? (
        <Row className='mb-5'>
          <Col>
            <Card className='card-custom card-border'>
              <Card.Body className='d-flex flex-column justify-content-center'>
                <h3 className='text-primary'>Tariffs</h3>
                <div className='text-gray-700'>
                  In this module, you can manage <strong>tariff data</strong> for{' '}
                  <strong>Bound</strong>, <strong>MFN</strong>, and <strong>Preferential</strong>{' '}
                  rates.
                  <br />
                  <br />➤ Use <strong>Search</strong> to retrieve tariff data by Reporter, Partner
                  (for Preferential), and HS Version.
                  <br />
                  {ability.can('create', subject) || ability.can('update', subject) ? (
                    <>
                      ➤ To add or update tariff data in bulk:
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;• First, download the Excel format here{' '}
                      <Link
                        to=''
                        className='text-link'
                        onClick={() => {
                          handleDownloadTemplate()
                        }}
                      >
                        Export Template
                      </Link>
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;• Fill in the tariff values
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;• Then upload the file here{' '}
                      <Button
                        className='btn btn-primary btn-inline'
                        onClick={() => {
                          setShowImportModal(true)
                        }}
                      >
                        Import Excel
                      </Button>
                    </>
                  ) : (
                    <>
                      ➤ To add or update tariff data in bulk, you need to have permission to create
                      or update tariff data.
                    </>
                  )}
                  <br />
                  {/* <i>*Both manual entry and bulk import are supported.</i> */}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <NoPermissionCard />
      )}

      {canAccess ? (
        <>
          <Row className='mb-5'>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title>
                    <span className='fa fa-magnifying-glass px-2'></span>
                    <h3 className='text-gray-700'>Search Economy Tariff(s)</h3>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <Row className='mb-5 d-flex align-items-end'>
                    <Col lg={4} md={4}>
                      <Form.Group>
                        <Form.Label>Reporter Economy</Form.Label>
                        <EconDropdown
                          econ={tariffParams.rep}
                          onSelect={(value) =>
                            setTariffParams({
                              ...tariffParams,
                              rep: value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col lg={4} md={4} className={clsx({'d-none': typeTariff !== 'pref'})}>
                      <Form.Group>
                        <Form.Label>Partner Economy</Form.Label>
                        <div>
                          <EconDropdown
                            econ={tariffParams.par}
                            onSelect={(value) =>
                              setTariffParams({
                                ...tariffParams,
                                par: value,
                              })
                            }
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col lg={4} md={4}>
                      <Form.Group>
                        <Form.Label>HS Version</Form.Label>
                        {/* <Form.Select value={hsVersion} onChange={(e) => setHSVersion(e.target.value)}> */}
                        <Form.Select
                          className='mt-2 pb-3'
                          size='sm'
                          value={tariffParams.hs_ver || ''}
                          onChange={(e) =>
                            setTariffParams({
                              ...tariffParams,
                              hs_ver: e.target.value,
                            })
                          }
                        >
                          <option value='' disabled>
                            Select HS Version
                          </option>
                          <option value='H4'>H4</option>
                          <option value='H5'>H5</option>
                          <option value='H6'>H6</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className='mb-5 justify-content-end'>
                    <Col xs='auto'>
                      <Button variant='light' className='me-2' size='sm' onClick={btnResetHandler}>
                        <i className='fa fa-refresh me-1'></i> Reset
                      </Button>
                      <Button
                        variant='primary'
                        size='sm'
                        onClick={btnSearchHandler}
                        disabled={!isSearchEnabled}
                      >
                        <i className='fa fa-search me-1'></i> Search
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className={clsx('mb-5', {'d-none': isShowTable === false})}>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100'>
                    <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                      <div className='title d-flex align-items-center'>
                        <span className='fa fa-file-alt px-2'></span>
                        <h3 className='text-gray-700'>List HS - {pageTitle}</h3>
                      </div>
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>{pageTable}</Card.Body>
              </Card>
            </Col>
          </Row>
          <ModalUploadTariff
            show={showUploadTariffModal}
            onHide={() => {
              setImportError(null)
              setShowImportModal(false)
            }}
            onSubmit={handleSubmitImport}
            onFileChange={setImportFile}
            // isLoading={importLoading}
            // error={importError}
            tariffType={pageTitle!}
            isLoading={isImporting}
            error={importError}
          />
        </>
      ) : (
        <NoPermissionCard />
      )}
    </>
  )
}

export default TariffPage
